import produce from "immer";
import { compact, isObject, omit, toInteger } from "lodash";
import { normalizePrice } from "shared/listing-helpers";

import { INGESTION_SOURCE_DATA_MAP } from 'constants/ingestion';

export function calculatePriceMargin({ wholesale: incomingWholesale, retail, margin, ignoreShopifyRetailer = false }) {
  if (toInteger(incomingWholesale) > 0 && !ignoreShopifyRetailer) {
    return { wholesale: normalizePrice(incomingWholesale), isWholesaleCalculatedFromMargin: false };
  }

  if (toInteger(margin) <= 0) {
    return { wholesale: normalizePrice(0), isWholesaleCalculatedFromMargin: false };
  }

  return { wholesale: normalizePrice(retail / margin), isWholesaleCalculatedFromMargin: true };
}

/**
 * Map listing/match API category strings to verification form category labels (/L1/L2/L3).
 * API values often start with "/" (e.g. "/Women/Tops/Crop Tops"). Naive split("/") then
 * takes the first segment as "" so the label becomes "//Women/Tops" and never matches the dropdown.
 *
 * @param {string | undefined} raw
 * @returns {string}
 */
export function listingCategoryPathToFormLabel(raw) {
  if (typeof raw !== "string" || !raw.trim()) {
    return "";
  }
  const segments = raw.split("/").map(s => s.trim()).filter(Boolean);
  if (segments.length >= 3) {
    return `/${segments[0]}/${segments[1]}/${segments.slice(2).join("/")}`;
  }
  const t = raw.trim();
  return t.startsWith("/") ? t : `/${t}`;
}

/**
 *
 * @param {Verification.ProductParsedData} productData
 * @return {Partial<Verification.FormData>}
 */

export function dataToDefaultFormValues({ connection_type, parsed_data }, normalizedSizes, retailerMargin) {
  return INGESTION_SOURCE_DATA_MAP[connection_type].parsedDataFormatter(
    parsed_data, normalizedSizes, retailerMargin
  )
}

/**
 *
 * @param {Verification.ProductConnection} productionConnection
 * @param {Partial<Verification.FormData>} dirtyFields
 * @param {Verification.FormData} formValues
 * @param {{variant_group_external_id: string} | {variant_group_id: number} | null} variantGroup
 * @returns {SubmitFormData}
 */
// eslint-disable-next-line max-params
export function formToConnectionModification(productionConnection, dirtyFields = {}, formValues = {}, variantGroup) {
  /** @type {Partial<Verification.FormData>} */
  const formData = dirtyValues(dirtyFields, formValues);

  const submitData = produce(omit(formData, ["category"]), draft => {
    if (draft.images) {
      draft.images = compact(draft.images);
    }
    if (draft.normalized_color && isObject(draft.normalized_color)) {
      draft.normalized_color = draft.normalized_color.value;
    }
    if (draft.brand && isObject(draft.brand)) {
      draft.brand = draft.brand.value;
    }
  });
  return {
    connection: productionConnection.connection_id,
    original: productionConnection.original_id,
    source_ids: productionConnection.source_ids,
    submitted_data: { ...submitData, ...variantGroup },
    ignored: false
  };
}

/**
 *
 * Helper function that takes the formState.dirtyFields and formValues from
 * handleSubmit() to only return the values that are "dirty" in the form instead
 * of all of the values from the form
 *
 * https://github.com/react-hook-form/react-hook-form/discussions/1991#discussioncomment-31308
 *
 * @typedef {unknown[] | Record<string, unknown>} UnknownArrayOrObject;
 * @param {UnknownArrayOrObject | boolean} dirtyFields
 * @param {UnknownArrayOrObject} allValues
 * @returns {UnknownArrayOrObject}
 */
export function dirtyValues(dirtyFields, allValues) {
  if (dirtyFields === true || Array.isArray(dirtyFields)) return allValues;
  return Object.fromEntries(Object.keys(dirtyFields).map(key => [key, dirtyValues(dirtyFields[key], allValues[key])]));
}

/**
 * In order to pass the variant group to the matched product, we need to make sure we did not change the three key identifiers
 * that make a product variant: brand + title + color. (skipping primary image check)
 *
 * Supports both variant_group_external_id (from matching service) and variant_group_id (from PIM search).
 * @param {{brand?: string, title?: string, color?: string, variant_group_external_id?: string, variant_group_id?: number} | null} matchingProduct
 * @param {Partial<Verification.FormData>} currentProduct
 * @returns {{variant_group_external_id: string} | {variant_group_id: number} | null}
 */
export function getVariantGroupId(matchingProduct, currentProduct) {
  if (
    !matchingProduct ||
    matchingProduct.brand.toLocaleLowerCase() !== currentProduct.brand.toLocaleLowerCase() ||
    matchingProduct.title.toLocaleLowerCase() !== currentProduct.title.toLocaleLowerCase() ||
    matchingProduct.color.toLocaleLowerCase() !== currentProduct.color.toLocaleLowerCase()
  ) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  if (matchingProduct.variant_group_external_id)
    return { variant_group_external_id: matchingProduct.variant_group_external_id };

  if (matchingProduct.variant_group_id)
    return { variant_group_id: matchingProduct.variant_group_id };

  // eslint-disable-next-line unicorn/no-null
  return null;
}

export function getNormalizedSizesAndCount(sizes, normalizedSizes) {
  let isAllStandardSizes = true;
  const normalizedSizesObject = sizes?.map(each => {
    const normalized = normalizedSizes[each.size];

    if (isAllStandardSizes && !normalized) {
      isAllStandardSizes = false;
    }
    return { ...each, normalized: normalized || "-" };
  });

  return { normalizedSizesObject, isAllStandardSizes };
}

/**
 * Collect unique incoming size labels from raw connection-request payloads
 * (before formatting) so we can query normalizedsizes?original_labels=...
 */
export function collectSizeLabelsFromConnectionResources(results) {
  const labels = new Set();
  for (const cr of results || []) {
    const parsed = cr?.parsed_data || {};
    const sizeVariants = parsed.size_variants;
    if (Array.isArray(sizeVariants)) {
      for (const variant of sizeVariants) {
        const size = variant?.size;
        // eslint-disable-next-line unicorn/no-null
        if (size !== undefined && size !== null && String(size).trim()) {
          labels.add(String(size).trim());
        }
      }
    }
    const sizes = parsed.sizes;
    if (Array.isArray(sizes)) {
      for (const entry of sizes) {
        const size = typeof entry === "string" ? entry : entry?.size;
        // eslint-disable-next-line unicorn/no-null
        if (size !== undefined && size !== null && String(size).trim()) {
          labels.add(String(size).trim());
        }
      }
    }
  }
  return [...labels];
}

export function formatVerificationConnections(verificationData, retailerMargin) {
  if (!verificationData?.connections?.data?.results?.length > 0 || !verificationData?.normalizedSizes) {
    return [];
  }

  return verificationData.connections.data.results.map(cr => {
    const formatted = dataToDefaultFormValues(cr, verificationData.normalizedSizes, retailerMargin);
    return {
      ...cr,
      formatted
    };
  });
}
